import { type Component, createSignal, createResource, For, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { Icon } from '@iconify-icon/solid'
import { useNavigate } from '@solidjs/router'
import { sticky } from '../../../shared/directives/sticky'
import { AppRoutes } from '../../../shared/app-routes'
import { useAdminEvents } from '../../../shared/providers/coris/coris-admin-events.provider'
import { useUser } from '../../../shared/providers/user-provider'
import type { BackendEvent } from '../data/api/admin-events.api-datasource'
import BulkCreateEventsDialog from './BulkCreateEventsDialog'
import DeleteEventDialog from './DeleteEventDialog'
import { downloadCsv } from '../utils/csv-export'
import { useNotification } from '../../../shared/notifications'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

// Prevent tree-shaking of directive
sticky

const PER_PAGE = 10

// startsAt is a floating calendar date — only the YYYY-MM-DD portion matters. Take the
// date part verbatim and ignore the time/zone entirely, so we display exactly the day
// the server sent (e.g. 2026-01-01 always shows Jan 1, 2026, regardless of time).
const formatDate = (iso: string): string => {
  return dayjs.utc(iso.slice(0, 10)).format('MMM D, YYYY')
}

const formatDateTime = (iso: string, tz?: string): string => {
  const d = tz ? dayjs(iso).tz(tz) : dayjs(iso).tz(dayjs.tz.guess())
  return d.format('MMM D, YYYY h:mm A')
}

const formatNumber = (n: number): string => n.toLocaleString()

const thClass =
  'px-3 py-2.5 text-left text-xs font-semibold text-primary-800 border-r border-b border-gray-200 whitespace-nowrap'
const tdClass = 'px-3 py-2 border-r border-b border-gray-200 text-sm text-gray-700 whitespace-nowrap'

const ImagePreview: Component<{ src: string; alt: string }> = (props) => {
  const [show, setShow] = createSignal(false)
  const [pos, setPos] = createSignal({ x: 0, y: 0 })
  let ref: HTMLImageElement | undefined

  const onEnter = (): void => {
    if (!ref) return
    const rect = ref.getBoundingClientRect()
    setPos({ x: rect.left + rect.width / 2, y: rect.top })
    setShow(true)
  }

  return (
    <>
      <img
        ref={ref}
        src={props.src}
        alt=""
        class="w-5 h-5 rounded-sm object-cover cursor-pointer"
        onMouseEnter={onEnter}
        onMouseLeave={() => setShow(false)}
      />
      <Show when={show()}>
        <Portal>
          <div
            class="fixed z-50 p-1 bg-white rounded-lg shadow-lg border border-gray-200 -translate-x-1/2 -translate-y-full pointer-events-none"
            style={{ left: `${pos().x}px`, top: `${pos().y - 8}px` }}
          >
            <img src={props.src} alt={props.alt} class="w-48 rounded object-contain max-h-48" />
          </div>
        </Portal>
      </Show>
    </>
  )
}

const AdminEventsPage: Component = () => {
  const navigate = useNavigate()
  const {
    getEvents,
    getRsvpList,
    getMeetAndGreetList,
    deleteEvent: deleteEventApi,
    exportMembersPresaleCodes
  } = useAdminEvents()
  const notification = useNotification()
  const { organizationSlug } = useUser()
  const [search, setSearch] = createSignal('')
  const [isStuck, setIsStuck] = createSignal(false)
  const [createOpen, setCreateOpen] = createSignal(false)
  const [bulkDialogOpen, setBulkDialogOpen] = createSignal(false)
  const [deleteEvent, setDeleteEvent] = createSignal<BackendEvent | null>(null)
  const [currentPage, setCurrentPage] = createSignal(1)

  const [allEvents, { mutate: mutateEvents, refetch: refetchEvents }] = createResource(
    () => organizationSlug(),
    async () => getEvents()
  )

  const filteredEvents = () => {
    const events = allEvents() ?? []
    const q = search().toLowerCase()
    const matched = !q
      ? events
      : events.filter(
          (e) =>
            e.title.toLowerCase().includes(q) ||
            (e.venue?.name ?? '').toLowerCase().includes(q) ||
            (e.venue?.address?.city ?? '').toLowerCase().includes(q)
        )
    // Order by calendar date (YYYY-MM-DD): upcoming events first, soonest at the top;
    // past events follow, most recent first. "Today" is the viewer's local calendar date.
    const today = dayjs().format('YYYY-MM-DD')
    const dateOf = (e: BackendEvent): string => e.startsAt.slice(0, 10)
    return [...matched].sort((a, b) => {
      const da = dateOf(a)
      const db = dateOf(b)
      const aUpcoming = da >= today
      const bUpcoming = db >= today
      if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1
      return aUpcoming ? da.localeCompare(db) : db.localeCompare(da)
    })
  }

  const totalResults = () => filteredEvents().length
  const totalPages = () => Math.max(1, Math.ceil(totalResults() / PER_PAGE))
  const from = () => (currentPage() - 1) * PER_PAGE + 1
  const to = () => Math.min(currentPage() * PER_PAGE, totalResults())

  const pagedEvents = () => {
    const start = (currentPage() - 1) * PER_PAGE
    return filteredEvents().slice(start, start + PER_PAGE)
  }

  // Reset to page 1 when search changes
  const exportEventsCsv = (): void => {
    const events = allEvents() ?? []
    const headers = [
      'Title',
      'Venue Name',
      'Presale Url',
      'Event Date',
      'City',
      'State',
      'Country',
      'Goes live At',
      'Image'
    ]
    const rows = events.map((e) => [
      e.title,
      e.venue?.name ?? '',
      e.features?.presale?.url ?? '',
      formatDate(e.startsAt),
      e.venue?.address?.city ?? '',
      e.venue?.address?.state ?? '',
      e.venue?.address?.country ?? '',
      e.publishAt ? formatDateTime(e.publishAt) : '',
      e.imageUrl ?? ''
    ])
    downloadCsv('events.csv', headers, rows)
  }

  const exportRsvpCsv = async (event: BackendEvent): Promise<void> => {
    const attendees = await getRsvpList(event.id)
    const headers = ['Venue Name', 'Name', 'Email']
    const rows = attendees.map((a) => [a.venueName ?? event.venue?.name ?? '', a.displayName ?? '', a.email ?? ''])
    const slug = event.title.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()
    downloadCsv(`${slug}-early-access-attendees.csv`, headers, rows)
  }

  const exportMeetAndGreetCsv = async (event: BackendEvent): Promise<void> => {
    const attendees = await getMeetAndGreetList(event.id)
    const headers = ['Venue Name', 'Name', 'Email']
    const rows = attendees.map((a) => [a.venueName ?? event.venue?.name ?? '', a.displayName ?? '', a.email ?? ''])
    const slug = event.title.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()
    downloadCsv(`${slug}-meet-and-greet-attendees.csv`, headers, rows)
  }

  const exportPresaleCodesCsv = (): void => {
    notification.progress(<span>Exporting presale codes...</span>, {
      task: () => exportMembersPresaleCodes(),
      onSuccess: () => <span>Presale codes exported</span>,
      onError: () => <span>Failed to export presale codes</span>
    })
  }

  const handleSearch = (value: string): void => {
    setSearch(value)
    setCurrentPage(1)
  }

  const getVisibility = (event: BackendEvent): { visible: boolean; reasons: string[] } => {
    const reasons: string[] = []
    if (event.active === false) reasons.push('Event is deactivated')
    if (event.endsAt && new Date(event.endsAt).getTime() < Date.now()) reasons.push('Event has ended')
    if (event.publishAt && new Date(event.publishAt).getTime() > Date.now())
      reasons.push('Publish date is in the future')
    if (reasons.length === 0) return { visible: true, reasons: ['Event is live'] }
    return { visible: false, reasons }
  }

  return (
    <div class="flex w-full flex-col min-h-full p-6">
      {/* Sticky filter bar */}
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 -mx-6 px-6 py-6 transition-colors duration-200"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent': !isStuck()
        }}
      >
        <div class="mx-auto w-full max-w-[1280px] flex items-start gap-8">
          {/* Search */}
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2.5 rounded-md border border-gray-200 bg-white px-3 py-2">
              <div class="text-gray-400">
                <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
              </div>
              <input
                type="text"
                placeholder="Search by event"
                value={search()}
                onInput={(e) => handleSearch(e.currentTarget.value)}
                class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          {/* Actions */}
          <div class="flex items-center gap-4">
            <button
              type="button"
              class="flex-shrink-0 px-3.5 py-2 bg-white rounded-full shadow-sm border border-gray-200 flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
              onClick={exportEventsCsv}
            >
              <Icon icon="heroicons:arrow-down-tray" class="text-[1rem]" />
              Export
            </button>
            <div class="relative">
              <button
                type="button"
                class="flex-shrink-0 px-3.5 py-2 bg-primary-600 text-white rounded-full shadow-sm flex items-center gap-1.5 text-sm font-semibold hover:bg-primary-700"
                onClick={() => setCreateOpen(!createOpen())}
              >
                <Icon icon="heroicons:wrench" class="text-[1rem]" />
                Manage
                <span class="transition-transform duration-200" classList={{ 'rotate-180': createOpen() }}>
                  <Icon icon="heroicons:chevron-down" class="text-[1rem]" />
                </span>
              </button>
              <Show when={createOpen()}>
                <div class="absolute right-0 top-full mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20 animate-menu-enter">
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setCreateOpen(false)
                      navigate(AppRoutes.AdminEventsCreate())
                    }}
                  >
                    Create Event
                  </button>
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setCreateOpen(false)
                      setBulkDialogOpen(true)
                    }}
                  >
                    Bulk Upload Events
                  </button>
                  <div class="my-1 border-t border-gray-200" />
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setCreateOpen(false)
                      navigate(AppRoutes.AdminPresalePoolCreate())
                    }}
                  >
                    Create Presale Unique Codes
                  </button>
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setCreateOpen(false)
                      navigate(AppRoutes.AdminPresalePoolEdit())
                    }}
                  >
                    Edit Presale Unique Codes
                  </button>
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setCreateOpen(false)
                      exportPresaleCodesCsv()
                    }}
                  >
                    Export Presale Codes
                  </button>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div class="mx-auto flex w-full max-w-[1280px] flex-col flex-1">
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          {/* Scrollable table */}
          <div class="overflow-x-auto table-scroll flex-1">
            <table class="min-w-full border-separate border-spacing-0 table-fixed">
              <colgroup>
                <col style={{ width: '44px' }} /> {/* Vis. */}
                <col style={{ width: '110px' }} /> {/* Date */}
                <col style={{ width: '180px' }} /> {/* Publish at */}
                <col style={{ width: '180px' }} /> {/* Title */}
                <col style={{ 'min-width': '200px' }} /> {/* Venue — flexes */}
                <col style={{ width: '100px' }} /> {/* City */}
                <col style={{ width: '56px' }} /> {/* State */}
                <col style={{ width: '90px' }} /> {/* Country */}
                <col style={{ width: '160px' }} /> {/* Presale URL */}
                <col style={{ width: '40px' }} /> {/* Img */}
                <col style={{ width: '90px' }} /> {/* Early Access */}
                <col style={{ width: '72px' }} /> {/* M&G */}
                <col style={{ width: '76px' }} /> {/* Actions */}
              </colgroup>
              <thead>
                <tr class="bg-gray-50">
                  <th class={`${thClass} text-center`}>Vis.</th>
                  <th class={thClass}>Date</th>
                  <th class={thClass}>Publish at</th>
                  <th class={thClass}>Title</th>
                  <th class={thClass}>Venue</th>
                  <th class={thClass}>City</th>
                  <th class={thClass}>State</th>
                  <th class={thClass}>Country</th>
                  <th class={thClass}>Presale URL</th>
                  <th class={thClass}>Img</th>
                  <th class={`${thClass} text-center`}>Early Access</th>
                  <th class={`${thClass} text-center border-r-0`}>M&G</th>
                  <th class="px-3 py-2.5 text-left text-xs font-semibold text-primary-800 border-b border-gray-200 whitespace-nowrap sticky right-0 bg-gray-50 border-l border-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <Show
                  when={allEvents() !== undefined}
                  fallback={
                    <For each={[1, 2, 3, 4, 5]}>
                      {() => (
                        <tr class="animate-pulse">
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-24" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-4 mx-auto" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-36" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-28" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-40" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-32" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-16" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-8" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-16" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-5" />
                          </td>
                          <td class={tdClass}>
                            <div class="h-5 bg-gray-200 rounded w-12 ml-auto" />
                          </td>
                          <td class={`${tdClass} border-r-0`}>
                            <div class="h-5 bg-gray-200 rounded w-8 ml-auto" />
                          </td>
                          <td class="px-3 py-2 border-b border-gray-200 sticky right-0 bg-white border-l border-gray-200">
                            <div class="h-5 bg-gray-200 rounded w-14" />
                          </td>
                        </tr>
                      )}
                    </For>
                  }
                >
                  <Show when={pagedEvents().length === 0}>
                    <tr>
                      <td colspan="13" class="px-4 py-12 text-center text-sm text-gray-500">
                        No events found.
                      </td>
                    </tr>
                  </Show>
                  <For each={pagedEvents()}>
                    {(event) => (
                      <tr class="hover:bg-gray-50">
                        {(() => {
                          const { visible, reasons } = getVisibility(event)
                          const [showTip, setShowTip] = createSignal(false)
                          const [pos, setPos] = createSignal({ x: 0, y: 0 })
                          let ref: HTMLSpanElement | undefined
                          const onEnter = (): void => {
                            if (!ref) return
                            const rect = ref.getBoundingClientRect()
                            setPos({ x: rect.left + rect.width / 2, y: rect.top })
                            setShowTip(true)
                          }
                          return (
                            <td class={`${tdClass} text-center`}>
                              <span
                                ref={ref}
                                class={`inline-flex cursor-help ${visible ? 'text-green-600' : 'text-red-500'}`}
                                onMouseEnter={onEnter}
                                onMouseLeave={() => setShowTip(false)}
                              >
                                <Icon icon={visible ? 'heroicons:check' : 'heroicons:x-mark'} class="text-[1rem]" />
                              </span>
                              <Show when={showTip()}>
                                <Portal>
                                  <div
                                    class="fixed z-50 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-md shadow-lg whitespace-nowrap pointer-events-none -translate-x-1/2 -translate-y-full"
                                    style={{ left: `${pos().x}px`, top: `${pos().y - 6}px` }}
                                  >
                                    <For each={reasons}>{(reason) => <div>{reason}</div>}</For>
                                    <div class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                  </div>
                                </Portal>
                              </Show>
                            </td>
                          )
                        })()}
                        <td class={tdClass}>{formatDate(event.startsAt)}</td>
                        <td class={tdClass}>
                          {event.publishAt ? (
                            formatDateTime(event.publishAt, event.timezone)
                          ) : (
                            <span class="text-gray-400">Immediate</span>
                          )}
                        </td>
                        <td class={`${tdClass} max-w-0 overflow-hidden truncate`} title={event.title}>
                          {event.title}
                        </td>
                        <td class={`${tdClass} max-w-0 overflow-hidden truncate`} title={event.venue?.name ?? ''}>
                          {event.venue?.name ?? ''}
                        </td>
                        <td
                          class={`${tdClass} max-w-0 overflow-hidden truncate`}
                          title={event.venue?.address?.city ?? ''}
                        >
                          {event.venue?.address?.city ?? ''}
                        </td>
                        <td class={tdClass}>{event.venue?.address?.state ?? ''}</td>
                        <td
                          class={`${tdClass} max-w-0 overflow-hidden truncate`}
                          title={event.venue?.address?.country ?? ''}
                        >
                          {event.venue?.address?.country ?? ''}
                        </td>
                        <td class={`${tdClass} max-w-0 overflow-hidden`}>
                          <Show when={event.features?.presale?.url}>
                            <a
                              href={event.features!.presale!.url!}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={event.features!.presale!.url!}
                              class="text-primary-600 underline hover:text-primary-700 block truncate"
                            >
                              {event.features!.presale!.url}
                            </a>
                          </Show>
                        </td>
                        <td class={tdClass}>
                          <Show when={event.imageUrl}>
                            <ImagePreview src={event.imageUrl!} alt={event.title} />
                          </Show>
                        </td>
                        <td class={tdClass}>
                          <div class="flex items-center justify-center gap-1">
                            <span class="tabular-nums">{formatNumber(event.features?.earlyAccess?.count ?? 0)}</span>
                            <button
                              type="button"
                              class="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                              title="Download Early Access attendees"
                              onClick={() => exportRsvpCsv(event)}
                            >
                              <Icon icon="heroicons:arrow-down-tray" class="text-[1rem]" />
                            </button>
                          </div>
                        </td>
                        <td class={`${tdClass} border-r-0`}>
                          <div class="flex items-center justify-center gap-1">
                            <span class="tabular-nums">{formatNumber(event.fanMeetAndGreet?.count ?? 0)}</span>
                            <button
                              type="button"
                              class="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                              title="Download Meet & Greet attendees"
                              onClick={() => exportMeetAndGreetCsv(event)}
                            >
                              <Icon icon="heroicons:arrow-down-tray" class="text-[1rem]" />
                            </button>
                          </div>
                        </td>
                        <td class="px-3 py-2 border-b border-gray-200 sticky right-0 bg-white border-l border-gray-200">
                          <div class="flex items-center gap-0.5">
                            <button
                              type="button"
                              class="w-7 h-7 rounded-md flex items-center justify-center text-gray-500 border border-transparent hover:border-gray-200 hover:shadow-sm transition-all"
                              title="Edit"
                              onClick={() => navigate(AppRoutes.AdminEventsEdit(event.id))}
                            >
                              <Icon icon="heroicons:pencil-20-solid" class="text-[1rem]" />
                            </button>
                            <button
                              type="button"
                              class="w-7 h-7 rounded-md flex items-center justify-center text-gray-500 border border-transparent hover:border-gray-200 hover:shadow-sm transition-all"
                              title="Delete"
                              onClick={() => setDeleteEvent(event)}
                            >
                              <Icon icon="heroicons:trash" class="text-[1rem]" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </Show>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div class="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between">
            <div class="text-sm text-gray-700">
              <Show
                when={allEvents() !== undefined}
                fallback={<div class="h-5 bg-gray-200 rounded w-48 animate-pulse" />}
              >
                <Show when={totalResults() > 0} fallback={<span>No results</span>}>
                  Showing <span class="font-semibold">{from()}</span> to <span class="font-semibold">{to()}</span> of{' '}
                  <span class="font-semibold">{totalResults()}</span> results
                </Show>
              </Show>
            </div>
            <Show when={allEvents() !== undefined && totalPages() > 1}>
              <div class="flex items-center rounded overflow-hidden border border-gray-200">
                <button
                  type="button"
                  class="w-9 h-9 flex items-center justify-center bg-white text-gray-500 hover:bg-gray-50 border-r border-gray-200 disabled:opacity-50"
                  disabled={currentPage() === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <Icon icon="heroicons:chevron-left" class="text-[1rem]" />
                </button>
                <For each={Array.from({ length: totalPages() }, (_, i) => i + 1)}>
                  {(page) => (
                    <button
                      type="button"
                      class="min-w-9 h-9 px-3 flex items-center justify-center text-sm font-medium border-r border-gray-200 last:border-r-0"
                      classList={{
                        'bg-primary-600 text-white font-semibold': currentPage() === page,
                        'bg-white text-gray-700 hover:bg-gray-50': currentPage() !== page
                      }}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )}
                </For>
                <button
                  type="button"
                  class="w-9 h-9 flex items-center justify-center bg-white text-gray-500 hover:bg-gray-50 border-l border-gray-200 disabled:opacity-50"
                  disabled={currentPage() === totalPages()}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages(), p + 1))}
                >
                  <Icon icon="heroicons:chevron-right" class="text-[1rem]" />
                </button>
              </div>
            </Show>
          </div>
        </div>
      </div>

      {/* Bulk create dialog */}
      <Show when={bulkDialogOpen()}>
        <BulkCreateEventsDialog onClose={() => setBulkDialogOpen(false)} onCreated={() => refetchEvents()} />
      </Show>

      {/* Delete confirmation dialog */}
      <Show when={deleteEvent()}>
        <DeleteEventDialog
          eventTitle={deleteEvent()!.title}
          onCancel={() => setDeleteEvent(null)}
          onConfirm={() => {
            const event = deleteEvent()!
            setDeleteEvent(null)
            // Optimistically remove from list
            mutateEvents((prev) => prev?.filter((e) => e.id !== event.id))
            notification.progress(<span>Deleting event...</span>, {
              task: () => deleteEventApi(event.id),
              onSuccess: () => <span>Event deleted</span>,
              onError: () => {
                // Revert on failure
                refetchEvents()
                return <span>Failed to delete event</span>
              }
            })
          }}
        />
      </Show>
    </div>
  )
}

export default AdminEventsPage
